import {useState, useEffect, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MessageSquare, Search, User, AlertTriangle, Send, MoreVertical, Trash2, RefreshCw, Shield, AlertCircle } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateFormat';
import PageTitle from '../../components/PageTitle';

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  onConfirm: () => void;
}

interface AdminChat {
  _id?: string;
  id: string;
  subject: string;
  status: 'active' | 'closed' | 'pending';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'billing' | 'technical' | 'general' | 'complaint' | 'feature_request';
  lastMessageAt: string;
  messageCount: number;
  isReadByUser: boolean;
  isReadByAdmin: boolean;
  user: {
    id: string;
    email: string;
  };
  admin?: {
    id: string;
    email: string;
  };
  createdAt: string;
}

interface AdminMessage {
  id: string;
  content: string;
  senderType: 'user' | 'admin';
  sender: {
    id: string;
    email: string;
  };
  isRead: boolean;
  createdAt: string;
}

export default function DashboardAdminChats() {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const { socket, connectionStatus, joinAdminChat, leaveAdminChat, sendTypingStart, sendTypingStop } = useSocket();
  const [chats, setChats] = useState<AdminChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<AdminChat | null>(null);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [isTyping, setIsTyping] = useState(false);
  const [userTyping, setUserTyping] = useState(false);
  const [openMenuChatId, setOpenMenuChatId] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);



  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket) {
      console.log('AdminDashboard: Socket not available yet');
      return;
    }
    
    console.log('AdminDashboard: Setting up socket listeners, socket connected:', socket.connected);

    const handleNewMessage = (messageData: any) => {
      console.log('[Chat Flow] 📩 AdminDashboard: Received new message via socket:', messageData);
      setMessages(prev => {
        // Check if message already exists
        const exists = prev.some(msg => msg.id === messageData.id);
        if (exists) return prev;

        console.log('[Chat Flow] ✅ AdminDashboard: Adding new message to state');
        return [...prev, {
          id: messageData.id,
          content: messageData.content,
          senderType: messageData.senderType,
          sender: messageData.sender,
          isRead: messageData.isRead,
          createdAt: messageData.createdAt,
          attachments: messageData.attachments || []
        }];
      });

      // Update chat's last message time and count
      setChats(prev => prev.map(chat =>
        (chat._id || chat.id) === messageData.chatId
          ? {
              ...chat,
              lastMessageAt: messageData.createdAt,
              messageCount: chat.messageCount + 1,
              isReadByAdmin: false
            }
          : chat
      ));

      // Scroll to bottom for new messages
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    };

    const handleUserTyping = (data: any) => {
      // Ignore own typing events
      if (data.userId === user?.id) return;
      
      setUserTyping(true);
    };

    const handleUserStoppedTyping = (data: any) => {
      // Ignore own typing events
      if (data.userId === user?.id) return;
      
      setUserTyping(false);
    };

    const handleChatUpdated = (update: any) => {
      setChats(prev => prev.map(chat =>
        (chat._id || chat.id) === update.chatId
          ? { ...chat, ...update.updates }
          : chat
      ));

      // Update selected chat if it's the current one
      if (selectedChat && (selectedChat._id || selectedChat.id) === update.chatId) {
        setSelectedChat(prev => prev ? { ...prev, ...update.updates } : null);
      }
    };

    // Handle new chat created by a user
    const handleNewChat = (chatData: any) => {
      console.log('AdminDashboard: New chat received:', chatData);
      toast.success(t('admin_chats.new_chat_from', { email: chatData.user.email }), { icon: '💬' });
      // Reload the chat list to include the new chat
      loadChats();
    };

    // Handle chat notification (new message, new chat, etc.)
    const handleChatNotification = (data: any) => {
      console.log('AdminDashboard: Chat notification received:', data);
      if (data.type === 'new_chat') {
        toast.success(t('admin_chats.new_request', { subject: data.chat?.subject || 'New chat' }), { icon: '🆕' });
        loadChats();
      } else if (data.type === 'new_message') {
        // If viewing this chat, add the message directly
        if (selectedChat && (selectedChat._id === data.chatId || selectedChat.id === data.chatId)) {
          if (data.message) {
            setMessages(prev => {
              const exists = prev.some(msg => msg.id === data.message.id);
              if (exists) return prev;
              return [...prev, {
                id: data.message.id,
                content: data.message.content,
                senderType: data.message.senderType,
                sender: data.message.sender,
                isRead: data.message.isRead,
                createdAt: data.message.createdAt,
                attachments: data.message.attachments || []
              }];
            });
            
            // Scroll to bottom
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }
        } else if (data.message?.senderType === 'user') {
          // Show notification if not viewing that chat
          toast.success(t('admin_chats.new_message_from', { email: data.message.sender.email }), { icon: '💬' });
        }
        
        // Update chat list
        loadChats();
      } else if (data.type === 'chat_deleted') {
        toast(t('admin_chats.user_deleted_chat'));
        // If currently viewing the deleted chat, clear selection
        if (selectedChat && String(selectedChat._id || selectedChat.id) === String(data.chatId)) {
          setSelectedChat(null);
          setMessages([]);
        }
        loadChats();
      }
    };

    socket.on('admin-message-received', handleNewMessage);
    socket.on('admin-user-typing', handleUserTyping);
    socket.on('admin-user-stopped-typing', handleUserStoppedTyping);
    socket.on('admin-chat-updated', handleChatUpdated);
    socket.on('new-admin-chat', handleNewChat);
    socket.on('admin-chat-notification', handleChatNotification);

    return () => {
      socket.off('admin-message-received', handleNewMessage);
      socket.off('admin-user-typing', handleUserTyping);
      socket.off('admin-user-stopped-typing', handleUserStoppedTyping);
      socket.off('admin-chat-updated', handleChatUpdated);
      socket.off('new-admin-chat', handleNewChat);
      socket.off('admin-chat-notification', handleChatNotification);
    };
  }, [socket, selectedChat]);

  // Join/leave chat rooms when selectedChat changes
  useEffect(() => {
    if (selectedChat && socket?.connected) {
      joinAdminChat(selectedChat._id || selectedChat.id);
    }

    return () => {
      if (selectedChat && socket?.connected) {
        leaveAdminChat(selectedChat._id || selectedChat.id);
      }
    };
  }, [selectedChat, joinAdminChat, leaveAdminChat, socket?.connected]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuChatId(null);
    };

    if (openMenuChatId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuChatId]);

  // Load admin chats
  const loadChats = async () => {
    try {
      const response = await axios.get('/api/admin/admin-chats');
      setChats(response.data.chats);
      setAccessDenied(false);
    } catch (error: any) {
      console.error('Failed to load admin chats:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(t('admin_chats.load_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Load messages for selected chat
  const loadMessages = async (chatId: string) => {
    try {
      const response = await axios.get(`/api/admin/admin-chats/${chatId}/messages`);
      setMessages(response.data.messages);
      setSelectedChat(response.data.chat);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error(t('admin_chats.load_messages_failed'));
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!selectedChat || !newMessage.trim()) return;

    // Stop typing indicator
    if (isTyping) {
      sendTypingStop(selectedChat._id || selectedChat.id);
      setIsTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    }

    setSending(true);
    try {
      await axios.post(`/api/admin/admin-chats/${selectedChat._id || selectedChat.id}/messages`, {
        content: newMessage.trim()
      });

      setNewMessage('');
      // Note: Real-time updates will handle message display and chat list updates
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(t('admin_chats.send_failed'));
    } finally {
      setSending(false);
    }
  };

  // Handle input changes with typing indicators
  const handleInputChange = (value: string) => {
    setNewMessage(value);

    if (!selectedChat) return;

    // Send typing update with content
    sendTypingStart(selectedChat._id || selectedChat.id);
    setIsTyping(true);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStop(selectedChat._id || selectedChat.id);
      setIsTyping(false);
    }, 1000); // Stop typing after 1 second of inactivity
  };

  // Update chat status
  const updateChatStatus = async (chatId: string, status: string) => {
    try {
      await axios.patch(`/api/admin/admin-chats/${chatId}`, { status });
      await loadChats();
      if ((selectedChat?._id || selectedChat?.id) === chatId) {
        setSelectedChat(prev => prev ? { ...prev, status: status as any } : null);
      }
      toast.success(t('admin_chats.status_updated'));
    } catch (error) {
      console.error('Failed to update chat status:', error);
      toast.error(t('admin_chats.status_update_failed'));
    }
  };

  // Assign admin to chat
  const assignToChat = async (chatId: string) => {
    try {
      // Get current user ID (assuming admin is logged in)
      const response = await axios.get('/api/auth/me');
      const adminId = response.data.user.id;

      await axios.patch(`/api/admin/admin-chats/${chatId}/assign`, { adminId });
      await loadChats();
      toast.success(t('admin_chats.chat_assigned'));
    } catch (error) {
      console.error('Failed to assign chat:', error);
      toast.error(t('admin_chats.assign_failed'));
    }
  };

  // Delete chat
  const deleteChat = (chatId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: t('admin_chats.delete_title'),
      message: t('admin_chats.delete_confirm'),
      confirmText: t('common:delete'),
      onConfirm: async () => {
        try {
          await axios.delete(`/api/admin/admin-chats/${chatId}`);
          await loadChats();

          // If the deleted chat was selected, clear the selection
          if ((selectedChat?._id || selectedChat?.id) === chatId) {
            setSelectedChat(null);
            setMessages([]);
          }

          toast.success(t('admin_chats.chat_deleted'));
          setOpenMenuChatId(null);
        } catch (error) {
          console.error('Failed to delete chat:', error);
          toast.error(t('admin_chats.delete_failed'));
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  // Filter chats
  const filteredChats = chats.filter(chat => {
    const matchesSearch = chat.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (chat.user?.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || chat.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || chat.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    loadChats();
  }, []);

  // Auto-refresh chats every 30 seconds to show new chats
  useEffect(() => {
    const interval = setInterval(() => {
      loadChats();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-refresh messages every 10 seconds as fallback for socket issues
  useEffect(() => {
    if (!selectedChat) return;

    const interval = setInterval(() => {
      loadMessages(selectedChat._id || selectedChat.id);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [selectedChat]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400';
      case 'active': return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400';
      case 'closed': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400';
      case 'high': return 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400';
      case 'medium': return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400';
      case 'low': return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show access denied message
  if (accessDenied) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center p-8 max-w-md">
          <div className="w-20 h-20 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-10 w-10 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-semibold text-foreground mb-3">
            {t('admin_chats.admin_required')}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t('admin_chats.admin_required_desc')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col md:flex-row bg-card rounded-xl border border-border overflow-hidden">
      <PageTitle titleKey="admin_chats.title" namespace="dashboard" />
      {/* Sidebar (hidden on mobile when a chat is open) */}
      <aside className={`w-full md:w-[380px] md:border-r border-border flex flex-col ${selectedChat ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-border bg-card">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-foreground truncate">{t('admin_chats.title')}</h2>
                <div className={`w-2 h-2 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-emerald-500' :
                  connectionStatus === 'connecting' ? 'bg-amber-500 animate-pulse' :
                  connectionStatus === 'error' ? 'bg-red-500' : 'bg-muted-foreground'
                }`} />
              </div>
              <p className="text-xs text-muted-foreground">
                {filteredChats.length} {t('admin_chats.shown')} • {chats.filter(c => !c.isReadByAdmin).length} {t('admin_chats.unread')}
              </p>
            </div>

            <button
              onClick={() => loadChats()}
              className="inline-flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm rounded-lg transition-colors"
              title={t('common:refresh')}
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">{t('common:refresh')}</span>
            </button>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('admin_chats.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              {(['all', 'pending', 'active', 'closed'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
                    statusFilter === s
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:bg-muted'
                  }`}
                >
                  {s === 'all' ? t('admin_chats.all_status') : t(`admin_chats.status_${s}`)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              {(['all', 'urgent', 'high', 'medium', 'low'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPriorityFilter(p)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
                    priorityFilter === p
                      ? 'bg-foreground text-background border-foreground'
                      : 'bg-background text-foreground border-border hover:bg-muted'
                  }`}
                >
                  {p === 'all' ? t('admin_chats.all_priority') : t(`admin_chats.priority_${p}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {filteredChats.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">{t('admin_chats.no_chats')}</p>
              <p className="text-sm mt-1">{t('admin_chats.no_chats_desc')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredChats.map((chat) => {
                const chatId = chat._id || chat.id;
                const isSelected = String(selectedChat?._id || selectedChat?.id) === String(chatId);
                const initials = (chat.user?.email || 'U').slice(0, 1).toUpperCase();

                return (
                  <div
                    key={chatId}
                    role="button"
                    tabIndex={0}
                    onClick={() => loadMessages(chatId)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') loadMessages(chatId); }}
                    className={`w-full text-left p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
                      isSelected ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                        !chat.isReadByAdmin ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                      }`}>
                        {initials}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground truncate">{chat.subject}</p>
                              {!chat.isReadByAdmin && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-primary/10 text-primary">
                                  {t('admin_chats.new_badge')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {chat.user?.email || t('common:unknown')}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDateTime(chat.lastMessageAt)}
                            </span>

                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuChatId(openMenuChatId === String(chatId) ? null : String(chatId));
                                }}
                                className="p-1.5 rounded-lg hover:bg-accent transition-colors"
                                aria-label={t('common:actions')}
                              >
                                <MoreVertical className="h-4 w-4 text-muted-foreground" />
                              </button>
                              {openMenuChatId === String(chatId) && (
                                <div className="absolute right-0 top-9 z-10 bg-card border border-border rounded-xl shadow-lg py-1 min-w-36">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteChat(chatId);
                                    }}
                                    className="w-full px-3 py-2 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 flex items-center gap-2 text-sm"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    {t('admin_chats.delete_chat')}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(chat.status)}`}>
                              {t(`admin_chats.status_${chat.status}`)}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(chat.priority)}`}>
                              {t(`admin_chats.priority_${chat.priority}`)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {chat.messageCount} {t('admin_chats.msgs')}
                            </span>
                          </div>

                          {isSelected && (
                            <span className="text-[11px] text-primary font-medium">
                              {t('admin_chats.viewing')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      {/* Main panel */}
      <main className={`flex-1 flex flex-col ${selectedChat ? 'flex' : 'hidden md:flex'}`}>
        {!selectedChat ? (
          <div className="flex-1 flex items-center justify-center bg-muted/30">
            <div className="text-center text-muted-foreground p-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-card border border-border flex items-center justify-center">
                <MessageSquare className="h-8 w-8 opacity-60" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{t('admin_chats.select_chat')}</h3>
              <p className="mt-1 text-sm">{t('admin_chats.select_chat_desc')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="p-4 border-b border-border bg-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedChat(null);
                        setMessages([]);
                      }}
                      className="md:hidden p-2 -ml-2 rounded-lg hover:bg-accent transition-colors"
                      aria-label={t('admin_chats.back_to_list')}
                    >
                      <MessageSquare className="h-5 w-5 text-muted-foreground" />
                    </button>
                    <h3 className="font-semibold text-foreground truncate">{selectedChat.subject}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(selectedChat.status)}`}>
                      {t(`admin_chats.status_${selectedChat.status}`)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground truncate">
                    {selectedChat.user?.email || t('common:unknown')}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {selectedChat.status === 'pending' && (
                    <button
                      onClick={() => assignToChat(selectedChat._id || selectedChat.id)}
                      className="px-3 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      {t('admin_chats.assign_to_me')}
                    </button>
                  )}
                  {selectedChat.status !== 'closed' && (
                    <select
                      value={selectedChat.status}
                      onChange={(e) => updateChatStatus(selectedChat._id || selectedChat.id, e.target.value)}
                      className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground"
                    >
                      <option value="pending">{t('admin_chats.status_pending')}</option>
                      <option value="active">{t('admin_chats.status_active')}</option>
                      <option value="closed">{t('admin_chats.status_closed')}</option>
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto bg-muted/30 p-4">
              <div className="space-y-3">
                {messages.map((message) => {
                  const isAdminMsg = message.senderType === 'admin';
                  return (
                    <div key={message.id} className={`flex ${isAdminMsg ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-lg px-4 py-3 border ${
                        isAdminMsg
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card text-foreground border-border'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <div className={`mt-2 text-[11px] flex items-center gap-2 ${
                          isAdminMsg ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}>
                          <span className="font-medium">{message.sender.email}</span>
                          <span>•</span>
                          <span>{formatDateTime(message.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {userTyping && (
                  <div className="flex justify-start">
                    <div className="bg-card border border-border rounded-lg px-4 py-2 text-sm text-muted-foreground italic">
                      {t('admin_chats.user_typing')}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Composer */}
            {selectedChat.status !== 'closed' ? (
              <div className="p-4 border-t border-border bg-card">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !sending && sendMessage()}
                    placeholder={t('admin_chats.reply_placeholder')}
                    className="flex-1 px-4 py-2.5 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-transparent"
                    disabled={sending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="px-4 py-2.5 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {sending ? (
                      <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">{t('admin_chats.send')}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 border-t border-border bg-muted/30 text-sm text-muted-foreground text-center">
                {t('admin_chats.chat_closed')}
              </div>
            )}
          </>
        )}
      </main>

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onKeyDown={e => e.key === 'Escape' && setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
          tabIndex={-1}
        >
          <div className="bg-card rounded-xl shadow-lg w-full max-w-md overflow-hidden border border-border">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-50 dark:bg-red-950/30 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-red-500 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground">
                    {confirmDialog.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {confirmDialog.message}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-accent rounded-lg transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={confirmDialog.onConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  {confirmDialog.confirmText || t('common:delete')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
